/* GASSISA
=========================================================================================
* Descripción: Recuperación de password dashboard tenant.
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*       1       | 16/07/2026 | Lic. José Luis Aragón Cervantes  | Recuperación por MFA y liga para n8n
 *      2       | 16/07/2026 | Lic. José Luis Aragón Cervantes  | HelperText por campo para errores de recuperación
 * ---------------------------------------------------------------------------------------
 */

import TextField from "../ui/TextField";
import { useEffect, useState } from "react";
import { Alert, Box, Button, Grid, InputAdornment, Paper, Typography } from "@mui/material";
import KeyIcon from "@mui/icons-material/LockOutlined";
import PersonIcon from "@mui/icons-material/PersonOutlineSharp";
import PasswordIcon from "@mui/icons-material/PasswordOutlined";
import EmailIcon from "@mui/icons-material/EmailOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useSearchParams } from "react-router-dom";

import LogoMain from "../../img/sizesoftx-logo-1-20.svg";
import {
  apiTenantAdminPasswordRecoverLinkConfirm,
  apiTenantAdminPasswordRecoverLinkRequest,
} from "../../settings/ApiConfig";
import { routeLogin } from "../../settings/routeConfig";
import { useSesion } from "../../context/System/SesionContext";

const emptyForm = {
  email: "",
  tenantKey: "",
  newPassword: "",
  confirmPassword: "",
};

const emptyFieldErrors = {
  email: "",
  tenantKey: "",
  newPassword: "",
  confirmPassword: "",
};

const licenseExample = "ARXS-9F58-3045-83EE";

export default function FrmRecuperacionPasswordTenant() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getTenantAdminLoginDefaults } = useSesion();
  const [form, setForm] = useState(emptyForm);
  const [fieldErrors, setFieldErrors] = useState(emptyFieldErrors);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("info");
  const [loading, setLoading] = useState(false);

  const token = searchParams.get("token") ?? "";
  const hasToken = token.trim().length > 0;

  useEffect(() => {
    let mounted = true;
    getTenantAdminLoginDefaults?.().then((defaults) => {
      if (!mounted) return;
      setForm((current) => ({
        ...current,
        email: defaults?.email ?? "",
        tenantKey: defaults?.tenantKey ?? "",
      }));
    });
    return () => { mounted = false; };
  }, [getTenantAdminLoginDefaults, hasToken]);

  const cleanInputValue = (name, value) => {
    if (name === "email") return value.replace(/[^a-zA-Z0-9@._+-]/g, "").slice(0, 180);
    if (name === "tenantKey") {
        let clean = value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 16);
        return clean.replace(/(\w{4})(?=\w)/g, "$1-");
    }

    if (name === "newPassword" || name === "confirmPassword") return value.replace(/[<>`\x00-\x1F\x7F]/g, "").slice(0, 128);
    return value;
  };

  const isValidEmail = (value) => /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value.trim());

  const getTenantKeyHelper = () => {
    if (fieldErrors.tenantKey) return fieldErrors.tenantKey;
    if (!form.tenantKey.trim()) return "Captura la licencia registrada para este dashboard.";
    if (!form.tenantKey.includes("-") && form.tenantKey.replace(/-/g, "").length >= 16) {
      return `Si no encuentra la licencia, intenta con guiones. Ejemplo: ${licenseExample}`;
    }
    return `Ejemplo: ${licenseExample}`;
  };


  const clearFieldError = (name) => {
    if (!fieldErrors[name]) return;
    setFieldErrors((current) => ({ ...current, [name]: "" }));
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: cleanInputValue(name, value) }));
    clearFieldError(name);
  };

  const validateRecoveryIdentity = () => {
    const errors = { ...emptyFieldErrors };
    const email = form.email.trim();
    const tenantKey = form.tenantKey.trim();

    if (!email) errors.email = "Captura el correo administrador registrado.";
    else if (!isValidEmail(email)) errors.email = "Captura un correo vélido. Ejemplo: usuario@empresa.com";

    if (!tenantKey) errors.tenantKey = "Captura la licencia tenant.";
    else if (!/^[A-Z0-9-]{4,120}$/.test(tenantKey)) errors.tenantKey = "La licencia solo acepta letras, numeros y guiones.";
    else if (!tenantKey.includes("-")) errors.tenantKey = `La licencia podria requerir guiones. Ejemplo: ${licenseExample}`;

    setFieldErrors((current) => ({ ...current, ...errors }));
    return !errors.email && !errors.tenantKey;
  };

  const validatePassword = () => {
    const errors = { ...emptyFieldErrors };
    if (form.newPassword.trim().length < 8 || /[<>`\x00-\x1F\x7F]/.test(form.newPassword)) {
      errors.newPassword = "Minimo 8 caracteres. No uses <, >, ` ni caracteres de control.";
    }
    if (form.newPassword !== form.confirmPassword) {
      errors.confirmPassword = "La confirmacion no coincide con el nuevo password.";
    }

    setFieldErrors((current) => ({ ...current, ...errors }));
    if (errors.newPassword || errors.confirmPassword) {
      setSeverity("warning");
      setMessage(errors.newPassword || errors.confirmPassword);
      return false;
    }
    return true;
  };

  const markIdentityNotFound = () => {
    setFieldErrors((current) => ({
      ...current,
      email: "Verifica que sea el correo del administrador dueño de la licencia.",
      tenantKey: `No se encontro el cliente con esa licencia. Revisa guiones. Ejemplo: ${licenseExample}`,
    }));
  };

  const postRecovery = async (url, body) => {
    setLoading(true);
    setMessage("");
    try {
      const responseApi = await fetch(url, {
        method: "POST",
        body: JSON.stringify(body),
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
      });
      const payload = await responseApi.json().catch(() => null);
      const result = payload?.result ?? payload;
      if (!responseApi.ok || payload?.hasError) {
        const apiMessage = payload?.message ?? payload?.Message ?? result?.message ?? `No fue posible recuperar password (${responseApi.status}).`;
        if (apiMessage.toLowerCase().includes("datos de recuperacion no vélidos")) markIdentityNotFound();
        setSeverity("error");
        setMessage(apiMessage);
        return null;
      }
      setSeverity("success");
      setMessage(result?.message ?? "Solicitud procesada correctamente.");
      setFieldErrors(emptyFieldErrors);
      return result;
    } catch (error) {
      setSeverity("error");
      setMessage(`No fue posible conectar con Seguridad: ${error?.message ?? "sin conexion"}`);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleRequestLink = async () => {
    if (!validateRecoveryIdentity()) {
      setSeverity("warning");
      setMessage("Revisa correo y licencia para solicitar la liga.");
      return;
    }
    const result = await postRecovery(apiTenantAdminPasswordRecoverLinkRequest, {
      tenantkey: form.tenantKey,
      email: form.email,
      returnurl: `${window.location.origin}/recuperaciontenantadmin`,
      appkey: "DASHBOARD",
      appversion: process.env.REACT_APP_VERSION || "1.0.0",
    });
  };

  const handleResetWithToken = async (event) => {
    event.preventDefault();
    if (!validatePassword()) return;
    const result = await postRecovery(apiTenantAdminPasswordRecoverLinkConfirm, {
      token,
      newpassword: form.newPassword,
      appkey: "DASHBOARD",
      appversion: process.env.REACT_APP_VERSION || "1.0.0",
    });
    if (result?.passwordupdated || result?.passwordUpdated) {
      setForm((current) => ({ ...current, newPassword: "", confirmPassword: "" }));
    }
  };

  return (
    <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: "100vh", p: 2 }}>
      <Grid item xs={12} sm={10} md={6} lg={4}>
        <Paper sx={{ p: 4 }}>
          <Box textAlign="center" sx={{ mb: 3 }}>
            <img src={LogoMain} alt="SizeSoftX" style={{ maxWidth: 260, width: "100%" }} />
            <Typography variant="h6" fontWeight="bold" sx={{ mt: 2 }}>
              Recuperar password dashboard
            </Typography>
          </Box>

          {message && <Alert severity={severity} sx={{ mb: 2 }}>{message}</Alert>}

          <form noValidate onSubmit={handleResetWithToken}>
            {!hasToken && (
              <>
                <TextField autoComplete="off"
                  name="email"
                  label="Correo administrador"
                  value={form.email}
                  onChange={handleChange}
                  fullWidth
                  margin="dense"
                  error={Boolean(fieldErrors.email)}
                  helperText={fieldErrors.email || "Correo del administrador dueño de la licencia."}
                  InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon /></InputAdornment> }}
                />
                <TextField autoComplete="off"
                  name="tenantKey"
                  label="Licencia SizeSoftX"
                  value={form.tenantKey}
                  onChange={handleChange}
                  fullWidth
                  margin="dense"
                  error={Boolean(fieldErrors.tenantKey)}
                  helperText={getTenantKeyHelper()}
                  placeholder="ARXS-41D1-B501-C805"
                  inputProps={{ maxLength: 19, spellCheck: false }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><KeyIcon /></InputAdornment> }}
                />
              </>
            )}

            {hasToken && (
              <>
                <TextField
                  name="newPassword"
                  label="Nuevo password"
                  value={form.newPassword}
                  onChange={handleChange}
                  fullWidth
                  margin="dense"
                  type="password"
                  autoComplete="off"
                  error={Boolean(fieldErrors.newPassword)}
                  helperText={fieldErrors.newPassword || "Minimo 8 caracteres."}
                  InputProps={{ startAdornment: <InputAdornment position="start"><PasswordIcon /></InputAdornment> }}
                />
                <TextField
                  name="confirmPassword"
                  label="Confirmar password"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  fullWidth
                  margin="dense"
                  type="password"
                  autoComplete="off"
                  error={Boolean(fieldErrors.confirmPassword)}
                  helperText={fieldErrors.confirmPassword || "Debe coincidir con el nuevo password."}
                  InputProps={{ startAdornment: <InputAdornment position="start"><PasswordIcon /></InputAdornment> }}
                />

                <Button type="submit" variant="contained" fullWidth sx={{ mt: 2 }} disabled={loading}>
                  Cambiar password
                </Button>
              </>
            )}
          </form>

          {!hasToken && (
            <Button startIcon={<EmailIcon />} fullWidth sx={{ mt: 1 }} disabled={loading} onClick={handleRequestLink}>
              Enviar liga por correo
            </Button>
          )}

          <Button startIcon={<ArrowBackIcon />} fullWidth sx={{ mt: 2 }} onClick={() => navigate(routeLogin)}>
            Regresar a login
          </Button>
        </Paper>
      </Grid>
    </Grid>
  );
}
